import { Button, Form, Spinner } from '@monksflow/monks-ui';
import type { ProjectDetail } from '@/entities/project';
import { FormSidePanel } from '@/shared/ui/layout';
import { UnsavedChangesModal } from '@/shared/ui/UnsavedChangesModal';
import { EditProjectFormFields } from './EditProjectFormFields';
import { useEditProjectForm } from './useEditProjectForm';
import { useTranslation } from 'react-i18next';

// The panel puts its actions in the sheet footer, which is a sibling of the body
// rather than a descendant of the <form>, so the submit button reaches the form
// by id instead of by nesting.
const FORM_ID = 'edit-project-form';

interface EditProjectModalProps {
  open: boolean;
  project: ProjectDetail;
  onClose?: () => void;
  onSuccess?: () => void;
}

export function EditProjectModal(props: EditProjectModalProps) {
  const { t } = useTranslation('projects');

  const form = useEditProjectForm({
    project: props.project,
    onSuccess: props.onSuccess,
    onClose: props.onClose,
  });

  // Closing is `onClose` alone — `useModalState` schedules the unmount behind
  // the exit animation. If the form is dirty, `handleClose()` opens the
  // unsaved-changes confirmation instead and defers the real close to the
  // confirm-leave path.
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (form.isSubmitting) return;
    form.handleClose();
  };

  const actions = (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={form.isSubmitting}
        onClick={form.handleClose}
      >
        {t('editProject.editProjectModal.cancel')}
      </Button>
      <Button
        type="submit"
        form={FORM_ID}
        variant="default"
        disabled={!form.isDirty || form.isSubmitting}
      >
        {form.isSubmitting && <Spinner />}
        {t('editProject.editProjectModal.saveChanges')}
      </Button>
    </>
  );

  const fields = (
    <Form {...form}>
      <form
        id={FORM_ID}
        onSubmit={(event) => {
          void form.submitForm(event);
        }}
        className="flex flex-1 flex-col gap-4"
      >
        <EditProjectFormFields disabled={form.isSubmitting} />
      </form>
    </Form>
  );

  return (
    <>
      <FormSidePanel
        open={props.open && !form.showUnsavedModal}
        onOpenChange={handleOpenChange}
        title={t('editProject.editProjectModal.editProject')}
        footer={actions}
      >
        {fields}
      </FormSidePanel>

      <UnsavedChangesModal
        open={form.showUnsavedModal}
        onCancel={form.handleCancelLeave}
        onConfirmLeave={form.handleConfirmLeave}
      />
    </>
  );
}
